import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators';
import { getAccountInformation, logWithAmplitude } from '../../../helpers/api/api';

import { CreateTopicRequest } from '../../../types';
import { StateController } from '../../../helpers/state/state-management/state-controller';
import accountInfo from '../../../helpers/state/account-info';
import { createTopicRequestFromTopic } from '../../../helpers/api/topics-api';
import featuresInfo from '../../../helpers/state/features-info';
import { ifDefined } from 'lit/directives/if-defined';
import organizationInfo from '../../../helpers/state/organization-info';
import topicData from '../../../helpers/state/topic-data';

@customElement('admin-zapier-integration-addon')
export class ZapierIntegration extends LitElement {
   amplitude_callsite = 'admin-zapier-integration-addon';

    @state()
    createTopicRequest: CreateTopicRequest = createTopicRequestFromTopic(topicData.topic);

    @state()
    showKey = false;

    private readonly bindTopicState = new StateController(this, topicData);

    private openZapierIntegration() {
        window.open('https://zapier.com/developer/public-invite/164970/2372d600c6536b526025c98181e6dc55/', '_blank');
        logWithAmplitude(this.amplitude_callsite, 'clicked zapier integration');
    }

    private getAccountSecret() {
        const accountInformation = getAccountInformation();

        // we're giving users their own secret so they can use it in proxi.
        // In the future we should make a separate secret for zapier or external integrations
        // but lets see if anyone uses this first.

        let secret = '';
        if (accountInformation != null && this.showKey) {
            secret = `${btoa(
                `${accountInformation.email}:${accountInformation.device_name}:${accountInformation.secret}`
            )}`;
        }
        return secret;
    }

    private copyKeyToClipboard() {
        const secret = this.getAccountSecret();
        if (secret) {
            void navigator.clipboard.writeText(secret);
        }
    }

    private copyEmailToClipboard() {
        void navigator.clipboard.writeText(accountInfo.email);
    }

    private copyTopicIdToClipboard() {
        void navigator.clipboard.writeText(topicData.topicId);
    }

    private copyOrgIdToClipboard() {
        if (!organizationInfo.id) {
            return;
        }
        void navigator.clipboard.writeText(organizationInfo.id.$oid);
    }

    private flipShowKey() {
        this.showKey = !this.showKey;
    }

    render() {
        return html`
            <div class="addon__container">
            <h4 class="sub-title warning">
                The Zapier integration is no longer being maintained. Proxi has a more flexible webhook tool in development. If you would like early (beta) access to the new webhook tool, please email
                help@proxi.co
            </h4>
            <h4 class="sub-title"> 1) Open the Proxi-Zapier Integration</h4>
            <mwc-button @click="${this.openZapierIntegration}" raised>Zapier-Proxi Beta Extension Link</mwc-button>
            <br><br>
            <h4 class="sub-title"> 2) Authenticate your connection to this map with the following information</h4>
            ${featuresInfo.paywallEnabled || featuresInfo.passwordEnabled ? '' : html`
                <p class="info-paragraph">
                    <b style="color:var(--main-invalid-color)">You must upgrade to Pro or Teams to use this feature.</b>
                </p>`
            }
            <div class="text-fields ${featuresInfo.zapierEnabled ? '' : 'disabled'}">
                <div class="key-area">
                    <mwc-textfield
                        outlined
                        type='text'
                        ?disabled=${true}
                        label="Email Address"
                        value="${accountInfo.email}"
                    ></mwc-textfield>
                    <mwc-icon @click="${this.copyEmailToClipboard}">content_copy</mwc-icon>
                </div>
                <div class="key-area">
                    <mwc-textfield
                        outlined
                        type=${this.showKey ? 'text' : 'password'}
                        label="Account Auth Key"
                        onclick
                        value="${this.showKey ? ifDefined(this.getAccountSecret()) : '********'}"
                    ></mwc-textfield>
                    ${featuresInfo.zapierEnabled ? html`
                        <mwc-icon @click="${this.flipShowKey}">${this.showKey ? 'visibility_off' : 'visibility'}</mwc-icon>
                    ` : ''}
                    <mwc-icon ?disabled=${!featuresInfo.zapierEnabled} @click="${featuresInfo.zapierEnabled ? this.copyKeyToClipboard : ''}">content_copy</mwc-icon>
                </div>
                <div class="key-area">
                    <mwc-textfield
                        outlined
                        type='text'
                        label="Map ID"
                        value="${topicData.topicId}"
                    ></mwc-textfield>
                    <mwc-icon @click="${this.copyTopicIdToClipboard}">content_copy</mwc-icon>
                </div>
                ${organizationInfo.id ? html`
                    <div class="key-area">
                        <mwc-textfield
                            outlined
                            type='text'
                            label="Workspace ID"
                            value="${organizationInfo.id.$oid}"
                        ></mwc-textfield>
                        <mwc-icon @click="${this.copyOrgIdToClipboard}">content_copy</mwc-icon>
                    </div>
                ` : ''}
            </div>
            <br><br>
            <span class="info-paragraph"> <b> <span class='warning'> Warning: </span> These auth keys are sensitive and should be kept private. Anyone who has access to them can edit any of your maps. </b> </span>
            If you think your topic key may have been compromised, reach out to help@proxi.co.
            </span>
            <br><br>
            <p class="info-paragraph">Did you know, Proxi does custom integrations with other CRMs and services? Reach out to help@proxi.co to learn more about our custom integration services.</p>
            </div>
            <error-toast id="errorToast"></error-toast>
		`;
    }

    static get styles() {
        return css`

            .title {
                color: #000;
                text-align: center;
                font-size: 18px;
                font-weight: 700;
                line-height: 130%;
                margin: 0;
                text-align: left;
            }

            .description {
                color: #091E2C;
                font-size: 16px;
                line-height: 150%;
                margin: 0;
            }

            .info-paragraph {
                color: var(--theme-input-color);
                margin-top: 0;
                margin-bottom: 0;
            }
            .addon__container {
                flex-direction: column;
                max-height: calc(100vh - 90px);
                overflow: hidden auto;
                margin-bottom: 1rem;
                padding-right: 30px;
            }

            .disabled {
                opacity: 0.5;
                background-color: var(--theme-light-background-color);
                color: var(--theme-input-color);
            }

            mwc-icon {
                cursor: pointer;
                margin: 5px;
            }

            .key-area {
                display: flex;
                flex-direction: row;
                align-items: center;
                margin: 1rem;
            }

            mwc-textfield {
                flex-grow: 1;
            }

            /* width */
            .addon__container::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            .addon__container::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            .addon__container::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .drawer_image {
                margin-top: 1rem;
                margin-bottom: 1rem;
            }

            .warning {
                color: var(--main-invalid-color);
            }
        `;
    }
}